import React, { useState, useEffect, useMemo, useCallback, memo, useRef, useLayoutEffect } from 'react';
import { apiGet } from '../../../utils/api';
import { FaComments, FaThumbtack, FaSearch, FaFilter, FaBell } from 'react-icons/fa';
import { db } from '../../../firebase';
import { collection, query, where, onSnapshot, getDocs, updateDoc, doc, orderBy, limit } from 'firebase/firestore';
import useAuthStore from '../../../store/authStore';
import useUserOnlineStatus from '../hooks/useUserOnlineStatus';
import { useSocket } from '../hooks/useSocket';
import chatCache from '../../../utils/cache';
import { useDarkMode } from '../../../contexts/DarkModeContext';

// ⚡️ PERFORMANCE OPTIMIZATION: Enhanced date parsing with caching
const dateCache = new Map();
function getSortableDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string' || typeof dateStr.split !== 'function') return new Date(0);
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Cache parsed dates
  if (dateCache.has(dateStr)) {
    return dateCache.get(dateStr);
  }
  
  if (!dateStr.includes('/')) {
    const parsed = new Date(dateStr);
    dateCache.set(dateStr, parsed);
    return parsed;
  }
  
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const parsed = new Date(parts.reverse().join('-'));
    dateCache.set(dateStr, parsed);
    return parsed;
  }
  
  const fallback = new Date(0);
  dateCache.set(dateStr, fallback);
  return fallback;
}

// ⚡️ PERFORMANCE OPTIMIZATION: Lazy image component with intersection observer
const LazyImage = memo(({ src, alt, className, onLoad, onError }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();
  const observerRef = useRef();

  useEffect(() => {
    if (!imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    onError?.();
  }, [onError]);

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {!isLoaded && isInView && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-xl" />
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
        />
      )}
    </div>
  );
});

LazyImage.displayName = 'LazyImage';

// ⚡️ PERFORMANCE OPTIMIZATION: Enhanced conversation hook with better caching
function useUserConversations(isAdmin, refreshKey, msgUnsubsRef) {
  const { user } = useAuthStore();
  const [conversations, setConversations] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [activityMap, setActivityMap] = useState({});        // convId -> lastMsgMillis
  const [lastMsgMap, setLastMsgMap] = useState({});          // convId -> last message text
  const conversationsRef = useRef([]);
  const unreadCountsRef = useRef({});
  const activityMapRef = useRef({});
  const lastMsgMapRef = useRef({});

  const updateActivityMap = useCallback((m) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => {
        setActivityMap(m);
        activityMapRef.current = m;
      });
    } else {
      setActivityMap(m);
      activityMapRef.current = m;
    }
  }, []);

  const updateLastMsgMap = useCallback((m) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => {
        setLastMsgMap(m);
        lastMsgMapRef.current = m;
      });
    } else {
      setLastMsgMap(m);
      lastMsgMapRef.current = m;
    }
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Use requestIdleCallback for non-critical updates
  const updateConversations = useCallback((newConversations) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => {
        setConversations(newConversations);
        conversationsRef.current = newConversations;
      });
    } else {
      setConversations(newConversations);
      conversationsRef.current = newConversations;
    }
  }, []);

  const updateUnreadCounts = useCallback((newCounts) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => {
        setUnreadCounts(newCounts);
        unreadCountsRef.current = newCounts;
      });
    } else {
      setUnreadCounts(newCounts);
      unreadCountsRef.current = newCounts;
    }
  }, []);

  useEffect(() => {
    if (!user || !user.email) return; // Add email check

    // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced cache loading
    const cachedConversations = chatCache.getCachedConversationList(user.uid);
    if (cachedConversations && cachedConversations.length > 0) {
      updateConversations(cachedConversations);
    }

    let q;
    if (isAdmin) {
      q = query(collection(db, 'sellerbuyerconversations'));
    } else {
      q = query(
        collection(db, 'sellerbuyerconversations'),
        where('members', 'array-contains', user.email)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      updateConversations(convs);
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Cache conversations asynchronously
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => {
          chatCache.cacheConversationList(user.uid, convs);
        });
      } else {
      chatCache.cacheConversationList(user.uid, convs);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [user, isAdmin, refreshKey, updateConversations, updateUnreadCounts, user?.email]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Separate effect for message listeners
  useEffect(() => {
    if (!user || !conversationsRef.current.length) return;



    // Clean up existing listeners
    if (msgUnsubsRef.current) {
      Object.values(msgUnsubsRef.current).forEach(unsub => unsub());
      msgUnsubsRef.current = {};
    }

    // Set up message listeners for each conversation
    conversationsRef.current.forEach(conv => {
      if (!conv.id) return;
      
      const messagesCol = collection(db, 'sellerbuyerconversations', conv.id, 'messages');
      
      msgUnsubsRef.current[conv.id] = onSnapshot(
        query(messagesCol, orderBy('timestamp', 'desc')),
        (snap) => {
          let unreadCount = 0;
          snap.docs.forEach(doc => {
            const data = doc.data();
            if (isAdmin) {
              if (
                data.from !== user.email &&
                data.from !== 'ytproudltd@gmail.com' &&
                data.readByAdmin !== true
              ) {
                unreadCount += 1;
              }
            } else {
              if (data.from !== user.email && (!data.readBy || !data.readBy.includes(user.email))) {
                unreadCount += 1;
              }
            }
          });
          
          // Update unread count for this specific conversation
          const currentCount = unreadCountsRef.current[conv.id] || 0;
          if (currentCount !== unreadCount) {
            const updatedCounts = { ...unreadCountsRef.current, [conv.id]: unreadCount };
            updateUnreadCounts(updatedCounts);
          }

          // Track latest message activity for sorting
          const latestDoc = snap.docs[0];
          if (latestDoc) {
            const ld = latestDoc.data();

            const ts = ld.timestamp;
            const lastMs = ts?.seconds
              ? ts.seconds * 1000
              : (typeof ts === 'number' ? ts : (ts?.toMillis ? ts.toMillis() : 0));

            const lastText = ld.sanitized || ld.text || '';

            if (activityMapRef.current[conv.id] !== lastMs) {
              updateActivityMap({ ...activityMapRef.current, [conv.id]: lastMs });
            }
            if (lastMsgMapRef.current[conv.id] !== lastText) {
              updateLastMsgMap({ ...lastMsgMapRef.current, [conv.id]: lastText });
            }
          }
        }
      );
    });

    return () => {
      if (msgUnsubsRef.current) {
        Object.values(msgUnsubsRef.current).forEach(unsub => unsub());
        msgUnsubsRef.current = {};
      }
    };
  }, [user, isAdmin, conversations, updateUnreadCounts]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized merge with stable reference
  const conversationsWithUnread = useMemo(() => {
    const toMillis = (v) => {
      if (!v) return 0;
      if (typeof v === 'number') return v;
      if (v?.seconds) return v.seconds * 1000;
      if (v?.toMillis) return v.toMillis();
      const d = getSortableDate(v); // your helper; returns Date
      return d instanceof Date ? d.getTime() : 0;
    };

    return conversations.map(conv => {
      const derivedLastMs =
        activityMap[conv.id] ||
        toMillis(conv.lastMessageTime) ||
        toMillis(conv.updatedAt) ||
        toMillis(conv.createdAt) ||
        toMillis(conv.date);

      return {
        ...conv,
        unreadCount: unreadCounts[conv.id] || 0,
        lastMessage: lastMsgMap[conv.id] ?? conv.lastMessage,
        lastMessageTime: activityMap[conv.id] ? new Date(activityMap[conv.id]) : conv.lastMessageTime,
        __sortMs: derivedLastMs, // <- single source of truth for ordering
      };
    });
  }, [conversations, unreadCounts, activityMap, lastMsgMap]);

  return conversationsWithUnread;
}

// ⚡️ PERFORMANCE OPTIMIZATION: Helper functions (moved inside component)
function getOtherParticipant(room, userEmail) {
  const others = (room.members || []).filter(email => email !== userEmail && email !== 'ytproudltd@gmail.com');
  return others[0] || 'Unknown User';
}

function getStatusBadgeClass(status) {
  if (!status) return 'bg-gray-400 text-white';
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-red-500 text-white';
    case 'transferring':
    case 'nuwanisuru':
    case 'nuwanis':
      return 'bg-yellow-500 text-black';
    case 'refunded':
      return 'bg-blue-500 text-white';
    case 'cancel':
      return 'bg-black text-white';
    default:
      return 'bg-gray-400 text-white';
  }
}

function getDisplayStatus(status) {
  if (!status) return '';
  const s = status.toLowerCase();
  if (s === 'nuwanisuru' || s === 'nuwanis') {
    return 'Transferring';
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

// ⚡️ PERFORMANCE OPTIMIZATION: Enhanced memoized conversation row component
const SidebarConversationRow = memo(({ conv, user, onSelectConversation, selectedConversationId, activeTab, isAdmin = false }) => {
  const { isDarkMode } = useDarkMode();
  
  const avatarBg = isDarkMode ? '2a2a2a' : 'dddddd';
  const avatarColor = isDarkMode ? 'ffffff' : '222222';
  const buildAvatar = useCallback((name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=${avatarBg}&color=${avatarColor}&rounded=true`, [avatarBg, avatarColor]);
  const isRealAvatar = useCallback((url) => {
    if (!url || typeof url !== 'string') return false;
    const u = url.trim();
    if (u.includes('ui-avatars.com') || u.includes('pravatar.cc')) return false;
    return u.startsWith('http') || u.startsWith('data:image/');
  }, []);
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced memoization with deep comparison
  const otherPartyIdentifier = useMemo(() => {
    if (activeTab === 'agent' && (conv.uid || conv.userUid)) {
      // For agent conversations, try to get both UID and email
      const uid = conv.uid || conv.userUid;
      const email = conv.userEmail || conv.email;
      return { uid, email };
    } else if (conv.buyer && conv.seller && user) {
      // For buyer/seller conversations, get the other party's info
      const isBuyer = conv.buyer.email === user.email;
      const otherParty = isBuyer ? conv.seller : conv.buyer;
      return {
        uid: otherParty.uid || otherParty.id,
        email: otherParty.email
      };
    }
    return null;
  }, [activeTab, conv.uid, conv.userUid, conv.userEmail, conv.email, conv.buyer?.email, conv.seller?.uid, conv.seller?.email, conv.buyer?.uid, conv.buyer?.email, user?.email]);

  const { online: isOtherOnline } = useUserOnlineStatus(otherPartyIdentifier);
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Stable callback reference
  const handleSelect = useCallback(async () => {
    const conversationData = {
      ...conv,
      name: conv.isAdmin ? (conv.name || conv.userName || conv.userEmail || 'Unknown User') : getOtherParticipant(conv, user?.email),
      avatar: (isRealAvatar(conv.userAvatar) ? conv.userAvatar : null) ||
              (isRealAvatar(conv.avatar) ? conv.avatar : null) ||
              buildAvatar(conv.userName || conv.userEmail || getOtherParticipant(conv, user?.email) || 'User')
    };
    onSelectConversation(conversationData);
  }, [conv, user, onSelectConversation, isRealAvatar, buildAvatar]);
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized status calculation
  const [orderStatus, setOrderStatus] = useState('');
  useEffect(() => {
    let ignore = false;
    const load = async () => {
      const oid = conv.orderId || conv.order_id;
      if (!oid) return;
      const key = String(oid);
      if (orderCache.has(key)) {
        if (!ignore) setOrderStatus(orderCache.get(key)?.status || '');
        return;
      }
      try {
        // For admin users, use the admin endpoint that returns all orders
        const tryEndpoints = isAdmin ? ['/api/orders/'] : ['/api/orders/my', '/api/orders/sales'];
        for (const ep of tryEndpoints) {
          const res = await apiGet(ep);
          if (!res.ok) continue;
          const list = await res.json();
          const found = Array.isArray(list) ? list.find(o => String(o.order_id) === key) : null;
          if (found) {
            orderCache.set(key, found);
            if (!ignore) setOrderStatus(found.status || '');
            break;
          }
        }
      } catch {}
    };
    load();
    return () => { ignore = true; };
  }, [conv.orderId, conv.order_id, isAdmin]);
  const status = useMemo(() => {
    const statusRaw = orderStatus || conv.status || conv.order_status || '';
    return statusRaw ? statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase() : '';
  }, [orderStatus, conv.status, conv.order_status]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized display name
  const displayName = useMemo(() => {
    if (activeTab === 'agent') {
      return conv.isAdmin
        ? (conv.name || conv.userName || conv.userEmail || 'Unknown User')
        : (conv.userEmail || getOtherParticipant(conv, user?.email));
    } else {
      return conv.buyer && conv.seller
        ? (conv.buyer.email === user?.email
            ? (conv.seller.name || conv.seller_name || conv.seller.username || conv.seller.email)
            : (conv.buyer.name || conv.buyer_name || conv.buyer.username || conv.buyer.email))
        : (conv.name || conv.userName || conv.buyerName || conv.userEmail || 'Unknown User');
    }
  }, [activeTab, conv, user]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized avatar URL
  const [resolvedAvatar, setResolvedAvatar] = useState(null);
  const avatarUrl = useMemo(() => {
    if (conv.buyer && conv.seller) {
      if (user && conv.buyer.email === user.email) {
        const av = resolvedAvatar || conv.seller?.avatar || conv.seller_avatar;
        return isRealAvatar(av) ? av : buildAvatar(conv.seller?.name || conv.seller_name || conv.seller?.username || 'Seller');
      } else {
        const av = resolvedAvatar || conv.buyer?.avatar || conv.buyer_avatar;
        return isRealAvatar(av) ? av : buildAvatar(conv.buyer?.name || conv.buyer_name || conv.buyer?.username || 'Buyer');
      }
    }
    const av = resolvedAvatar || conv.userAvatar || conv.avatar;
    return isRealAvatar(av) ? av : buildAvatar(conv.userName || conv.userEmail || getOtherParticipant(conv, user?.email) || 'User');
  }, [conv, user, isRealAvatar, buildAvatar, resolvedAvatar]);

  // Try resolving avatar from backend if we only have initials
  useEffect(() => {
    if (isRealAvatar(resolvedAvatar)) return;
    let candidateName = '';
    if (conv.buyer && conv.seller) {
      if (user && conv.buyer.email === user.email) {
        candidateName = conv.seller?.username || conv.seller_name || conv.seller?.name || '';
      } else {
        candidateName = conv.buyer?.username || conv.buyer_name || conv.buyer?.name || '';
      }
    } else {
      candidateName = conv.userName || conv.name || '';
    }
    const currentAv = (conv.buyer && conv.seller)
      ? (user && conv.buyer.email === user.email ? (conv.seller?.avatar || conv.seller_avatar) : (conv.buyer?.avatar || conv.buyer_avatar))
      : (conv.userAvatar || conv.avatar);
    if (isRealAvatar(currentAv)) return;
    const username = (candidateName || '').toString().trim();
    if (!username) return;
    let aborted = false;
    (async () => {
      try {
        const res = await apiGet(`/api/users/public/${encodeURIComponent(username)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (aborted) return;
        if (data && isRealAvatar(data.avatar)) {
          setResolvedAvatar(data.avatar);
        }
      } catch (_) {}
    })();
    return () => { aborted = true; };
  }, [conv, user, isRealAvatar]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized last message display
  const lastMessageDisplay = useMemo(() => {
    // Handle case where lastMessage might be an object
    const messageText = typeof conv.lastMessage === 'string' ? conv.lastMessage : 
                       (conv.lastMessage?.sanitized || conv.lastMessage?.text || '');
    
    if (conv.isAdmin) {
      return messageText || '';
    }
    
    if ((!messageText || messageText.trim() === '') && conv.buyer && conv.seller && user) {
      return conv.buyer.email === user.email
        ? 'Your conversation has started 🎉 You can now start chatting with the seller.'
        : 'Your conversation has started 🎉 You can now start chatting with the buyer.';
    }
    
    return messageText || '—';
  }, [conv.lastMessage, conv.buyer, conv.seller, user, conv.isAdmin]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized date display
  const dateDisplay = useMemo(() => {
    const ms =
      (conv.lastMessageTime?.seconds ? conv.lastMessageTime.seconds * 1000
        : (conv.lastMessageTime instanceof Date ? conv.lastMessageTime.getTime()
        : conv.__sortMs)) || 0;

    return ms ? new Date(ms).toLocaleDateString() : '';
  }, [conv.lastMessageTime, conv.__sortMs]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized order ID display
  const orderIdDisplay = useMemo(() => {
    if (conv.isAdmin) return '';
    const oid = conv.orderId || conv.order_id || '';
    if (!oid) return '';
    return oid.toString().startsWith('#') ? oid : `#${oid}`;
  }, [conv.orderId, conv.order_id, conv.isAdmin]);

  if (conv.isAdmin) {
    return (
      <div
        key={conv.id}
        className={`group relative flex items-center gap-3 px-3 sm:px-4 py-3 cursor-pointer transition-all duration-200 rounded-2xl mb-2 border border-transparent ${
          isDarkMode 
            ? 'hover:bg-[#141414] hover:border-gray-800' 
            : 'hover:bg-gray-100 hover:border-gray-200'
        } ${selectedConversationId === conv.id ? (isDarkMode ? 'bg-[#151515]' : 'bg-gray-100') : ''} hover:shadow-md`}
        style={{ transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease' }}
        onClick={handleSelect}
        tabIndex={0}
        aria-label="Open conversation with Chat with Agent"
      >
        <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full ${
          isDarkMode ? 'bg-purple-900/40' : 'bg-purple-100'
        }`}>
          <FaComments className={`text-xl sm:text-2xl ${
            isDarkMode ? 'text-purple-400' : 'text-purple-500'
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className={`font-bold text-base sm:text-lg truncate flex items-center gap-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Chat with Agent
              <span className={`ml-1 sm:ml-2 inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full ${
                isDarkMode ? 'bg-purple-900/40' : 'bg-purple-100'
              }`}>
                <FaThumbtack className={`text-xs ${
                  isDarkMode ? 'text-purple-400' : 'text-purple-500'
                }`} />
              </span>
            </span>
          </div>
          <div className={`text-xs sm:text-sm mt-1 truncate ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {lastMessageDisplay}
          </div>
          <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs">
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{dateDisplay}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      key={conv.id}
      className={`group relative flex items-center gap-3 px-3 sm:px-4 py-3 cursor-pointer transition-all duration-200 rounded-2xl mb-2 border border-transparent ${
        isDarkMode 
          ? 'hover:bg-[#141414] hover:border-gray-800' 
          : 'hover:bg-gray-100 hover:border-gray-200'
      } ${selectedConversationId === conv.id ? (isDarkMode ? 'bg-[#151515]' : 'bg-gray-100') : ''} hover:shadow-md`}
      style={{ transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease' }}
      onClick={handleSelect}
      tabIndex={0}
      aria-label={`Open conversation with ${displayName}`}
    >
      <div className="relative flex-shrink-0">
        <LazyImage
          src={avatarUrl}
          alt={displayName}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
            />
            <span className={`absolute bottom-1 right-1 w-3 h-3 rounded-full border-2 border-white ${isOtherOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
                      <span className={`font-medium text-base sm:text-lg truncate flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {displayName}
            {activeTab === 'agent' && conv.isAdmin && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'
              }`}>
                <FaThumbtack className="mr-0" />
              </span>
            )}
          </span>
          {/* Show status for transaction conversations */}
          {(conv.orderId || conv.order_id) && status && (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getStatusBadgeClass(status)}`}>
              {getDisplayStatus(status)}
            </span>
          )}
        </div>
        <div className={`text-xs sm:text-sm mt-1 truncate ${
          conv.unreadCount > 0 
            ? (isDarkMode ? 'font-semibold text-white' : 'font-semibold text-gray-900') 
            : (isDarkMode ? 'text-gray-300' : 'text-gray-600')
        }`}>
          {lastMessageDisplay}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs">
          {/* Show order id for transaction conversations */}
          {!conv.isAdmin && (conv.orderId || conv.order_id) && orderIdDisplay && (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              isDarkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-100 text-gray-700'
            }`}>
              {orderIdDisplay}
            </span>
          )}
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{dateDisplay}</span>
          {conv.unreadCount > 0 && (
            <span className="ml-auto inline-flex items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold shadow-sm min-w-[22px] h-[22px] px-1.5">
              {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // ⚡️ PERFORMANCE OPTIMIZATION: Custom comparison for deep equality
  const prevLast = (prevProps.conv.lastMessage?.sanitized ?? prevProps.conv.lastMessage ?? '');
  const nextLast = (nextProps.conv.lastMessage?.sanitized ?? nextProps.conv.lastMessage ?? '');
  return (
    prevProps.conv.id === nextProps.conv.id &&
    prevProps.conv.unreadCount === nextProps.conv.unreadCount &&
    prevProps.conv.status === nextProps.conv.status &&
    prevProps.conv.__sortMs === nextProps.conv.__sortMs &&
    prevLast === nextLast &&
    prevProps.selectedConversationId === nextProps.selectedConversationId &&
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.user?.email === nextProps.user?.email
  );
});

SidebarConversationRow.displayName = 'SidebarConversationRow';

// In-memory cache for order lookups (avoid duplicate requests per session)
const orderCache = new Map(); // key: order_id (string) -> order object

// ⚡️ PERFORMANCE OPTIMIZATION: Enhanced memoized main Sidebar component
const Sidebar = memo(({ transactions, messages, moveConversationToTop, onSelectConversation, selectedConversationId, activeTab, setActiveTab, sidebarWidth, showAgentTab, showSampleAgentCard = true, agentConversations, sidebarLoading = false, isAdmin = false }) => {
  const { isDarkMode } = useDarkMode();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const { user } = useAuthStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const { onMessage, connect, disconnect } = useSocket({ enabled: true });
  const msgUnsubsRef = useRef({});
  const searchTimeoutRef = useRef(null);
  const avatarBg = isDarkMode ? '2a2a2a' : 'dddddd';
  const avatarColor = isDarkMode ? 'ffffff' : '222222';

  // ⚡️ PERFORMANCE OPTIMIZATION: Debounced search with stable reference
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
    }, 150); // Debounce search input
  }, []);

  const handleFilterChange = useCallback((e) => {
    setFilterStatus(e.target.value);
  }, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, [setActiveTab]);

  // Helper to build avatar URL
  const buildAvatar = useCallback((name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=${avatarBg}&color=${avatarColor}&rounded=true`, [avatarBg, avatarColor]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  const chatRooms = useUserConversations(isAdmin, refreshKey, msgUnsubsRef);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized data preparation
  const data = useMemo(() => {
  if (activeTab === 'transactions' || activeTab === 'messages' || activeTab === 'all' || activeTab === 'unread' || !activeTab) {
    const hasAgent = chatRooms.some(conv => conv.id === 'admin');
      return hasAgent ? chatRooms : [
      {
        id: 'admin',
        name: 'Chat with Agent',
        date: new Date().toLocaleDateString(),
        online: true,
        avatar: 'https://i.pravatar.cc/40?img=1',
        lastMessage: 'How can I help you today?',
        unreadCount: 0,
        isAdmin: true
      },
      ...chatRooms
    ];
  } else if (activeTab === 'agent') {
      return agentConversations || (chatRooms || []).filter(conv => conv.isAdmin && conv.id !== 'admin');
    }
    return [];
  }, [activeTab, chatRooms, agentConversations]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced filtered data calculation
  const filteredData = useMemo(() => {
  const lowerSearch = search.toLowerCase();
    return data.filter(conv => {
    if ((search.trim() !== '' || filterStatus !== 'all') && conv.isAdmin) return false;
      
    const hasOrder = !!(conv.orderId || conv.order_id);
      
    if (activeTab === 'messages') {
      if (conv.isAdmin) return true;
      if (hasOrder) return false;
    }
    if (activeTab === 'transactions') {
      if (conv.isAdmin) return true;
      if (!hasOrder) return false;
    }

      
    const matchesSearch =
      (conv.name && conv.name.toLowerCase().includes(lowerSearch)) ||
      (conv.userName && conv.userName.toLowerCase().includes(lowerSearch)) ||
      (conv.buyerName && conv.buyerName.toLowerCase().includes(lowerSearch)) ||
        (conv.seller?.name && conv.seller.name.toLowerCase().includes(lowerSearch)) ||
      (conv.seller_name && conv.seller_name.toLowerCase().includes(lowerSearch)) ||
        (conv.buyer?.name && conv.buyer.name.toLowerCase().includes(lowerSearch)) ||
      (conv.buyer_name && conv.buyer_name.toLowerCase().includes(lowerSearch)) ||
      (!conv.isAdmin && String(conv.orderId ?? '').toLowerCase().includes(lowerSearch));
      
    const matchesStatus =
      conv.isAdmin ||
      filterStatus === 'all' ||
      (conv.status && conv.status.toLowerCase() === filterStatus.toLowerCase());
      
    return matchesSearch && matchesStatus;
  });
  }, [data, search, filterStatus, activeTab]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced unread count calculations
  const { transactionsUnread, messagesUnread, agentUnread, hasAnyUnread, totalUnread } = useMemo(() => {
  const transactionsUnread = chatRooms
    .filter(conv => !!(conv.orderId || conv.order_id))
    .reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);

  const messagesUnread = chatRooms
    .filter(conv => !conv.isAdmin && !(conv.orderId || conv.order_id))
    .reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);

  const agentUnread = (agentConversations || []).reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);

  const hasAnyUnread = transactionsUnread > 0 || messagesUnread > 0 || agentUnread > 0;
  const totalUnread = transactionsUnread + messagesUnread + agentUnread;

    return { transactionsUnread, messagesUnread, agentUnread, hasAnyUnread, totalUnread };
  }, [chatRooms, agentConversations, isAdmin]);

  // ⚡️ GLOBAL UNREAD TOTAL BROADCASTING: Broadcast total unread count to floating chat icon
  useEffect(() => {
    // Set global variable for immediate access
    window.__unreadTotal = totalUnread;
    
    // Dispatch custom event for real-time updates
    const event = new CustomEvent('unreadTotalChanged', { 
      detail: totalUnread 
    });
    window.dispatchEvent(event);
  }, [totalUnread]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced sorted conversations with stable keys
  const sortedConversations = useMemo(() => {
    const list = filteredData
      .slice()
      .sort((a, b) => {
        // 1) Keep admin/pinned at very top
        if (!!a.isAdmin !== !!b.isAdmin) return a.isAdmin ? -1 : 1;

        // 2) Newest activity first
        const byTime = (b.__sortMs || 0) - (a.__sortMs || 0);
        if (byTime !== 0) return byTime;

        // 3) Tiebreaker: unread above read (rare tie case)
        const aUnread = a.unreadCount > 0, bUnread = b.unreadCount > 0;
        if (aUnread !== bUnread) return bUnread ? 1 : -1;

        return 0;
      });

    return list.map(conv => (
      <SidebarConversationRow
        key={conv.id} // keep keys stable for perf
        conv={conv}
        user={user}
        onSelectConversation={onSelectConversation}
        selectedConversationId={selectedConversationId}
        activeTab={activeTab}
        isAdmin={isAdmin}
      />
    ));
  }, [filteredData, user, onSelectConversation, selectedConversationId, activeTab, isAdmin]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized helper functions
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'Completed':
        return 'border-red-500';
      case 'Transferring':
        return 'border-yellow-500';
      case 'Refunded':
        return 'border-blue-500';
      case 'Cancel':
        return 'border-black';
      default:
        return 'border-gray-300';
    }
  }, []);

  const getPaymentStatusClass = useCallback((paymentStatus) => {
    switch (paymentStatus) {
      case 'Paid':
        return 'text-green-600 font-semibold';
      case 'Unpaid':
        return 'text-red-500 font-semibold';
      default:
        return 'text-gray-500';
    }
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced responsive handling with requestIdleCallback
  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    const handleResize = () => {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(checkMobile);
      } else {
        checkMobile();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSidebarWidth = useCallback(() => {
    if (isMobile) {
      return 'w-full';
    }
    const allowedSidebarWidths = {
      '330': 'w-[330px]',
      '380': 'w-[380px]',
      '480': 'w-[480px]',
      '490': 'w-[490px]',
      '525': 'w-[525px]',
    };
    return allowedSidebarWidths[String(sidebarWidth)] || 'w-[380px]';
  }, [isMobile, sidebarWidth]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized loading skeleton
  const loadingSkeleton = useMemo(() => (
    <div className="flex flex-col gap-3 sm:gap-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className={`animate-pulse flex items-center gap-3 p-3 sm:p-4 mb-2 sm:mb-3 rounded-2xl border ${
          isDarkMode ? 'bg-[#2a2a2a]/80 border-gray-700/50' : 'bg-white/80 border-gray-200'
        }`}>
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${
            isDarkMode ? 'bg-gray-600' : 'bg-gray-200'
          }`} />
          <div className="flex-1">
            <div className={`h-3 sm:h-4 rounded w-1/2 mb-2 ${
              isDarkMode ? 'bg-gray-600' : 'bg-gray-200'
            }`}></div>
            <div className={`h-2 sm:h-3 rounded w-1/3 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}></div>
          </div>
        </div>
      ))}
    </div>
  ), [isDarkMode]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized empty state
  const emptyState = useMemo(() => (
    <div className={`flex flex-col items-center justify-center h-full ${
      isDarkMode ? 'text-gray-400' : 'text-gray-500'
    }`}>
      <FaSearch className="text-3xl sm:text-4xl mb-2 opacity-50" />
      <p className="text-sm sm:text-base">No conversations found</p>
    </div>
  ), [isDarkMode]);

  return (
    <aside className={`${getSidebarWidth()} ${isDarkMode ? 'bg-gradient-to-b from-[#0f0f0f] to-[#0f0f0f]' : 'bg-gradient-to-b from-white to-gray-50'} ${isMobile ? '' : (isDarkMode ? 'border-r border-gray-700/50' : 'border-r')} flex flex-col h-full ${isDarkMode ? 'shadow-2xl shadow-black/50' : 'shadow-lg'}`}>
      {/* Modern Header - Mobile Optimized */}
      <div className={`px-4 sm:px-6 py-4 sm:py-6 border-b ${
        isDarkMode ? 'border-gray-700/50 bg-gradient-to-r from-[#0f0f0f] to-[#0f0f0f]' : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white'
      }`}>
        <div className="flex items-center justify-between mb-4">
                      <span className={`text-xl sm:text-2xl font-bold ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>Messages</span>
                      <button className={`p-2 rounded-full transition-colors relative ml-2 ${
            isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'
          }`}>
              <FaBell className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
            {hasAnyUnread && (
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
        </div>
        {/* Enhanced Search Bar - Mobile Optimized */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className={isDarkMode ? 'text-gray-400' : 'text-gray-400'} />
          </div>
          <input
            type="text"
            placeholder="Search conversations..."
            className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all duration-200 text-sm sm:text-base ${
              isDarkMode 
                ? 'bg-[#2a2a2a] border-gray-700/50 text-white placeholder-gray-400' 
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
            }`}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* Modern Tab Design - Mobile Optimized */}
                      <div className={`px-3 sm:px-4 py-3 sm:py-4 border-b ${
        isDarkMode ? 'border-gray-700/50 bg-[#0f0f0f]' : 'border-gray-200 bg-white'
      }`}>
          <div className={`flex gap-1 sm:gap-2 p-1 rounded-xl ${
            isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'
          }`}>
          <button
            className={`flex-1 min-w-[0] py-2 sm:py-2.5 px-4 sm:px-5 rounded-full font-medium text-xs sm:text-sm border transition-all duration-200 ${
              (activeTab === 'all' || !activeTab)
                ? (isDarkMode ? 'bg-[#2a2a2a] text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300 shadow-sm')
                : (isDarkMode ? 'bg-transparent text-gray-300 border-gray-700/50 hover:text-white' : 'bg-transparent text-gray-600 border-gray-300 hover:text-gray-900')
            }`}
            onClick={() => handleTabChange('all')}
          >
            <span className="flex items-center justify-center">All</span>
          </button>
          <button
            className={`flex-1 min-w-[0] py-2 sm:py-2.5 px-4 sm:px-5 rounded-full font-medium text-xs sm:text-sm border transition-all duration-200 ${
              activeTab === 'transactions' 
                ? (isDarkMode ? 'bg-[#2a2a2a] text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300 shadow-sm') 
                : (isDarkMode ? 'bg-transparent text-gray-300 border-gray-700/50 hover:text-white' : 'bg-transparent text-gray-600 border-gray-300 hover:text-gray-900')
            }`}
            onClick={() => handleTabChange('transactions')}
          >
            <span className="flex items-center justify-center">
              <span className="text-xs sm:text-sm">Transactions</span>
              {transactionsUnread > 0 && (
                <span className="ml-1 sm:ml-2 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[20px] sm:min-w-[24px] h-[20px] sm:h-[24px] px-1 sm:px-1.5">{transactionsUnread > 99 ? '99+' : transactionsUnread}</span>
              )}
            </span>
          </button>

          <button
            className={`flex-1 min-w-[0] py-2 sm:py-2.5 px-4 sm:px-5 rounded-full font-medium text-xs sm:text-sm border transition-all duration-200 ${
              activeTab === 'messages' 
                ? (isDarkMode ? 'bg-[#2a2a2a] text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300 shadow-sm') 
                : (isDarkMode ? 'bg-transparent text-gray-300 border-gray-700/50 hover:text-white' : 'bg-transparent text-gray-600 border-gray-300 hover:text-gray-900')
            }`}
            onClick={() => handleTabChange('messages')}
          >
            <span className="flex items-center justify-center">
              Messages
              {messagesUnread > 0 && (
                <span className="ml-1 sm:ml-2 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[20px] sm:min-w-[24px] h-[20px] sm:h-[24px] px-1 sm:px-1.5">{messagesUnread > 99 ? '99+' : messagesUnread}</span>
              )}
            </span>
          </button>
          {showAgentTab && (
            <button
              className={`flex-1 min-w-[0] py-2 sm:py-2.5 px-4 sm:px-5 rounded-full font-medium text-xs sm:text-sm border transition-all duration-200 ${
                activeTab === 'agent' 
                  ? (isDarkMode ? 'bg-[#2a2a2a] text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300 shadow-sm') 
                  : (isDarkMode ? 'bg-transparent text-gray-300 border-gray-700/50 hover:text-white' : 'bg-transparent text-gray-600 border-gray-300 hover:text-gray-900')
              }`}
              onClick={() => handleTabChange('agent')}
            >
              <span className="flex items-center justify-center">
                Agent
                {agentUnread > 0 && (
                  <span className="ml-1 sm:ml-2 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[20px] sm:min-w-[24px] h-[20px] sm:h-[24px] px-1 sm:px-1.5">{agentUnread > 99 ? '99+' : agentUnread}</span>
                )}
              </span>
            </button>
          )}
        </div>
      </div>
      {/* Status Filter: Only show in Transactions tab - Mobile Optimized */}
      {activeTab === 'transactions' && (
        <div className={`px-3 sm:px-4 py-3 border-b ${
          isDarkMode ? 'border-gray-700/50 bg-[#0f0f0f]' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center gap-2">
            <FaFilter className={isDarkMode ? 'text-gray-300' : 'text-gray-400'} />
            <select 
              className={`flex-1 px-3 py-2 rounded-lg border text-xs sm:text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200 ${
                isDarkMode 
                  ? 'bg-[#2a2a2a] border-gray-700/50 text-white' 
                  : 'bg-gray-50 border-gray-200 text-gray-900'
              }`}
              value={filterStatus} 
              onChange={handleFilterChange}
            >
              <option value="all">All Status</option>
              <option value="Completed">Completed</option>
              <option value="Transferring">Transferring</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>
        </div>
      )}
      <div className={`flex-1 overflow-y-auto custom-scrollbar p-3 sm:p-4 ${
        isDarkMode ? 'bg-gradient-to-br from-[#0f0f0f] to-[#0f0f0f]' : 'bg-gradient-to-br from-gray-50 to-gray-100'
      }`}>
        {sidebarLoading ? loadingSkeleton : filteredData.length === 0 ? emptyState : sortedConversations}
      </div>
    </aside>
  );
}, (prevProps, nextProps) => {
  // ⚡️ PERFORMANCE OPTIMIZATION: Custom comparison for deep equality
  return (
    prevProps.selectedConversationId === nextProps.selectedConversationId &&
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.sidebarLoading === nextProps.sidebarLoading &&
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.showAgentTab === nextProps.showAgentTab &&
    prevProps.sidebarWidth === nextProps.sidebarWidth &&
    prevProps.user?.email === nextProps.user?.email &&
    prevProps.agentConversations?.length === nextProps.agentConversations?.length
  );
});


Sidebar.displayName = 'Sidebar';

export default Sidebar;
